// apps/provider/src/services/api/notifications.js
// Domain module for notification system CRUD operations
//
// Methods:
//   Triggers: getNotificationTriggers, seedNotificationTriggers
//   Rules: getNotificationRules, getNotificationRule, createNotificationRule, updateNotificationRule, deleteNotificationRule
//   Steps: createNotificationStep, updateNotificationStep, deleteNotificationStep, reorderNotificationSteps
//   Monitoring: getNotificationQueue, getNotificationLog
//
// Response types: @prism-ehr/shared → NotificationTrigger, NotificationRuleWithTrigger,
//   NotificationRuleDetail, NotificationStep, NotificationQueueItemWithDetails, NotificationLogEntry

/**
 * Creates notification API methods.
 * Bound to ApiClient instance via Object.assign in index.js.
 *
 * @returns {Object} Notification methods
 */
export function createNotificationsMethods() {
  return {
    // ============================================================
    // Triggers
    // ============================================================

    /**
     * Get all active notification triggers.
     *
     * Xano: GET /notification-triggers
     * Xano staging file: notification-triggers_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').NotificationTrigger[]>}
     */
    getNotificationTriggers() {
      return this.request('/notification-triggers', { method: 'GET' });
    },

    /**
     * Seed notification triggers (admin utility, idempotent).
     *
     * Xano: POST /notification-triggers/seed
     * Xano staging file: notification-triggers_seed_POST.xs
     *
     * @returns {Promise<{triggers_processed: number, message: string}>}
     */
    seedNotificationTriggers() {
      return this.request('/notification-triggers/seed', { method: 'POST' });
    },

    // ============================================================
    // Rules CRUD
    // ============================================================

    /**
     * Get notification rules for the current organization.
     *
     * Xano: GET /notification-rules
     * Xano staging file: notification-rules_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.trigger_key] - Filter by trigger key
     * @param {boolean} [filters.is_active] - Filter by active status
     * @returns {Promise<import('@prism-ehr/shared').NotificationRuleWithTrigger[]>}
     */
    getNotificationRules(filters = {}) {
      const params = new URLSearchParams();
      if (filters.trigger_key) params.set('trigger_key', filters.trigger_key);
      if (filters.is_active !== undefined) params.set('is_active', String(filters.is_active));
      const qs = params.toString();
      return this.request(`/notification-rules${qs ? '?' + qs : ''}`, { method: 'GET' });
    },

    /**
     * Get a single notification rule with trigger details and all steps.
     *
     * Xano: GET /notification-rules/{notification_rule_id}
     * Xano staging file: notification-rules_notification_rule_id_GET.xs
     *
     * @param {string} ruleId
     * @returns {Promise<import('@prism-ehr/shared').NotificationRuleDetail>}
     */
    getNotificationRule(ruleId) {
      return this.request(`/notification-rules/${ruleId}`, { method: 'GET' });
    },

    /**
     * Create a new notification rule.
     *
     * Xano: POST /notification-rules
     * Xano staging file: notification-rules_POST.xs
     *
     * @param {Object} data
     * @param {string} data.trigger_id - ID of the trigger to bind to
     * @param {string} data.name - Rule name
     * @param {string} [data.description]
     * @param {string} data.recipient_type - "patient", "provider", or "both"
     * @param {boolean} [data.is_active=true]
     * @param {number} [data.priority=0]
     * @param {Object} [data.conditions]
     * @returns {Promise<import('@prism-ehr/shared').NotificationRuleWithTrigger>}
     */
    createNotificationRule(data) {
      return this.request('/notification-rules', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update a notification rule.
     *
     * Xano: PATCH /notification-rules/{notification_rule_id}
     * Xano staging file: notification-rules_notification_rule_id_PATCH.xs
     *
     * @param {string} ruleId
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').NotificationRuleWithTrigger>}
     */
    updateNotificationRule(ruleId, data) {
      return this.request(`/notification-rules/${ruleId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a notification rule and all its steps.
     *
     * Xano: DELETE /notification-rules/{notification_rule_id}
     * Xano staging file: notification-rules_notification_rule_id_DELETE.xs
     *
     * @param {string} ruleId
     * @returns {Promise<void>}
     */
    deleteNotificationRule(ruleId) {
      return this.request(`/notification-rules/${ruleId}`, { method: 'DELETE' });
    },

    // ============================================================
    // Steps CRUD
    // ============================================================

    /**
     * Create a new notification step within a rule.
     *
     * Xano: POST /notification-steps
     * Xano staging file: notification-steps_POST.xs
     *
     * @param {Object} data
     * @param {string} data.rule_id
     * @param {number} [data.step_order]
     * @param {string} data.timing_type - "immediate", "before", "after", or "specific_time"
     * @param {number} [data.delay_minutes]
     * @param {string} data.channel - "email" or "sms"
     * @param {string} [data.subject]
     * @param {string} data.body_template
     * @param {Object} [data.cancel_condition]
     * @param {boolean} [data.is_active=true]
     * @returns {Promise<import('@prism-ehr/shared').NotificationStep>}
     */
    createNotificationStep(data) {
      return this.request('/notification-steps', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update a notification step.
     *
     * Xano: PATCH /notification-steps/{notification_step_id}
     * Xano staging file: notification-steps_notification_step_id_PATCH.xs
     *
     * @param {string} stepId
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared').NotificationStep>}
     */
    updateNotificationStep(stepId, data) {
      return this.request(`/notification-steps/${stepId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a notification step.
     *
     * Xano: DELETE /notification-steps/{notification_step_id}
     * Xano staging file: notification-steps_notification_step_id_DELETE.xs
     *
     * @param {string} stepId
     * @returns {Promise<void>}
     */
    deleteNotificationStep(stepId) {
      return this.request(`/notification-steps/${stepId}`, { method: 'DELETE' });
    },

    /**
     * Reorder notification steps within a rule.
     *
     * Xano: POST /notification-steps/reorder
     * Xano staging file: notification-steps_reorder_POST.xs
     *
     * @param {string} ruleId
     * @param {string[]} stepIds - Ordered array of step IDs
     * @returns {Promise<import('@prism-ehr/shared').NotificationStep[]>}
     */
    reorderNotificationSteps(ruleId, stepIds) {
      return this.request('/notification-steps/reorder', {
        method: 'POST',
        body: JSON.stringify({ rule_id: ruleId, step_ids: stepIds }),
      });
    },

    // ============================================================
    // Monitoring
    // ============================================================

    /**
     * Get notification queue items for monitoring.
     * Broadcast fan-out rows (trigger_key = email_broadcast) are excluded server-side.
     *
     * Xano: GET /notification-queue
     * Xano staging file: 1243_notification_queue_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.status]
     * @param {string} [filters.trigger_key]
     * @param {string} [filters.channel] - "email" or "sms"
     * @param {string} [filters.patient_id] - Matches event_data.patient_id
     * @param {string} [filters.appointment_id] - Matches event_data.appointment_id
     * @param {number} [filters.date_from] - ms epoch, against scheduled_send_at
     * @param {number} [filters.date_to] - ms epoch, against scheduled_send_at
     * @param {string} [filters.sort_dir] - "asc" or "desc" (default) on scheduled_send_at
     * @param {number} [filters.page=1]
     * @param {number} [filters.per_page=25]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<import('@prism-ehr/shared').NotificationQueueItemWithDetails>>}
     */
    getNotificationQueue(filters = {}) {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.trigger_key) params.set('trigger_key', filters.trigger_key);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.patient_id) params.set('patient_id', filters.patient_id);
      if (filters.appointment_id) params.set('appointment_id', filters.appointment_id);
      if (filters.date_from) params.set('date_from', String(filters.date_from));
      if (filters.date_to) params.set('date_to', String(filters.date_to));
      if (filters.sort_dir) params.set('sort_dir', filters.sort_dir);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.per_page) params.set('per_page', String(filters.per_page));
      const qs = params.toString();
      return this.request(`/notification-queue${qs ? '?' + qs : ''}`, { method: 'GET' });
    },

    /**
     * Get notification log entries for auditing.
     *
     * Xano: GET /notification-log
     * Xano staging file: notification-log_GET.xs
     *
     * @param {Object} [filters]
     * @param {string} [filters.trigger_key]
     * @param {string} [filters.status]
     * @param {string} [filters.channel]
     * @param {number} [filters.page=1]
     * @param {number} [filters.per_page=25]
     * @returns {Promise<import('@prism-ehr/shared').NotificationLogEntry[]>}
     */
    getNotificationLog(filters = {}) {
      const params = new URLSearchParams();
      if (filters.trigger_key) params.set('trigger_key', filters.trigger_key);
      if (filters.status) params.set('status', filters.status);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.per_page) params.set('per_page', String(filters.per_page));
      const qs = params.toString();
      return this.request(`/notification-log${qs ? '?' + qs : ''}`, { method: 'GET' });
    },
  };
}
